import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { usePageMeta } from "@/lib/usePageMeta";
import { UmbrellaLogo } from "@/components/UmbrellaLogo";
import {
  Gift, Percent, Lock, Wallet, CheckCircle2, ShieldCheck, Briefcase,
  AlertTriangle, ArrowRight,
} from "lucide-react";

const ESCROW_STEPS = [
  [Wallet, "Pay into escrow", "The client pays the agreed quote into escrow before any work starts."],
  [Lock, "Held securely", "Funds are held securely in CAD via Stripe — neither side can touch them yet."],
  [CheckCircle2, "Released on completion", "When the client confirms the work is done right, the funds are released to the business."],
];

export default function Fees() {
  usePageMeta("Fees & Payments", "Posting a job is free. Clients pay a 3% service fee and businesses a 15% commission, with payments held safely in escrow.");
  const navigate = useNavigate();

  return (
    <div className="bg-white" data-testid="fees-page">
      {/* Hero */}
      <section className="relative brand-grid-bg">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 pt-16 pb-10 text-center">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-amber-600 bg-amber-500/10 px-3 py-1.5 rounded-full">
            <UmbrellaLogo size={14} /> Fees &amp; Payments
          </span>
          <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-950 leading-[1.05] text-balance">
            Simple, transparent <span className="text-amber-600">pricing</span>.
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl mx-auto">
            Posting a job is free for clients. You only pay when a job is completed through Black Umbrella,
            and every payment is protected by secure escrow.
          </p>
        </div>
      </section>

      {/* Fee cards */}
      <section className="max-w-5xl mx-auto px-4 sm:px-8 py-10">
        <div className="grid md:grid-cols-3 gap-6">
          <div data-testid="fee-free-posting" className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="h-12 w-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
              <Gift size={22} className="text-emerald-600" />
            </div>
            <h3 className="mt-4 font-heading text-lg font-bold text-slate-950">Posting a job is free</h3>
            <p className="mt-1 text-slate-600">Clients can post a job, compare custom quotes and chat with businesses at no cost.</p>
          </div>

          <div data-testid="fee-client-service" className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="h-12 w-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
              <Percent size={22} className="text-amber-600" />
            </div>
            <h3 className="mt-4 font-heading text-lg font-bold text-slate-950">
              Client service fee <span className="text-amber-600">3%</span>
            </h3>
            <p className="mt-1 text-slate-600">When a job is completed through Black Umbrella, the client pays a <strong>3%</strong> service fee on the job total.</p>
          </div>

          <div data-testid="fee-business-commission" className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="h-12 w-12 rounded-xl bg-slate-900/5 flex items-center justify-center">
              <Briefcase size={22} className="text-slate-900" />
            </div>
            <h3 className="mt-4 font-heading text-lg font-bold text-slate-950">
              Business commission <span className="text-amber-600">15%</span>
            </h3>
            <p className="mt-1 text-slate-600">When a job is completed through Black Umbrella, the business pays a <strong>15%</strong> commission on the job total.</p>
          </div>
        </div>
      </section>

      {/* Escrow steps */}
      <section className="bg-slate-950 text-white py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-8">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-center">How escrow works</h2>
          <p className="mt-3 text-center text-slate-300 max-w-2xl mx-auto">Your money is protected at every step.</p>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {ESCROW_STEPS.map(([Icon, title, body], i) => (
              <div key={title} data-testid={`escrow-step-${i + 1}`} className="rounded-2xl bg-white/5 border border-white/10 p-6">
                <div className="flex items-center gap-3">
                  <span className="h-9 w-9 rounded-full bg-amber-500 text-slate-950 font-bold flex items-center justify-center">{i + 1}</span>
                  <Icon size={20} className="text-amber-400" />
                </div>
                <h3 className="mt-4 font-heading text-lg font-bold">{title}</h3>
                <p className="mt-1 text-slate-300">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Protection + disputes */}
      <section className="max-w-5xl mx-auto px-4 sm:px-8 py-14 space-y-6">
        <div data-testid="protect-clients" className="flex items-start gap-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="shrink-0 h-12 w-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
            <ShieldCheck size={22} className="text-blue-600" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-bold text-slate-950">How escrow protects clients</h3>
            <p className="mt-1 text-slate-600">
              Your payment is held securely and is only released to the business once you confirm the work is
              done right. You never pay for work that hasn't been completed to your satisfaction.
            </p>
          </div>
        </div>

        <div data-testid="protect-businesses" className="flex items-start gap-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="shrink-0 h-12 w-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
            <Briefcase size={22} className="text-emerald-600" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-bold text-slate-950">How escrow protects businesses</h3>
            <p className="mt-1 text-slate-600">
              Because the client funds the full quote into escrow before you start, you know the money is there
              and committed. No chasing invoices — once the work is confirmed, you get paid.
            </p>
          </div>
        </div>

        <div data-testid="disputes" className="flex items-start gap-5 rounded-2xl border border-amber-200 bg-amber-50 p-6 shadow-sm">
          <div className="shrink-0 h-12 w-12 rounded-xl bg-amber-500/15 flex items-center justify-center">
            <AlertTriangle size={22} className="text-amber-600" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-bold text-slate-950">Disputes</h3>
            <p className="mt-1 text-slate-700">
              If something goes wrong, either side can open a dispute before funds are released and the
              Black Umbrella team will help resolve it.
            </p>
          </div>
        </div>

        <div className="pt-4 text-center">
          <Button data-testid="fees-post-job-cta" onClick={() => navigate("/post-job")}
            className="h-12 px-6 rounded-full bg-slate-950 hover:bg-amber-600 text-white text-base font-semibold">
            Post a job — it's free <ArrowRight size={18} className="ml-1" />
          </Button>
        </div>
      </section>
    </div>
  );
}
